import { MAX_CREDIBLE_CUT_PERCENT as CUT_PCT } from '@/lib/priceCredibility';
import type { PriceEvent } from '@/lib/api';

export { MAX_CREDIBLE_CUT_PERCENT } from '@/lib/priceCredibility';


/**
 * The size of one step, as a whole percent of the LARGER of the two prices.
 *
 * Measured against the larger price in both directions on purpose. For a
 * reduction that is arithmetically identical to the Go gate — there the larger
 * price is `OriginalListPrice` and the expression is
 * `(OriginalListPrice - Price) * 100 / OriginalListPrice`. For an *increase* it
 * catches the same dropped digit recovering: $58,000 → $580,000 is the same
 * corrupt record read forwards.
 *
 * `Math.trunc` because the Go side is integer division and truncates. Without it
 * a 60.4% step would be suspect here and credible there, and a boundary that
 * disagrees across two surfaces is worse than either boundary.
 */
export function stepMovePercent(from: number, to: number): number {
  const hi = Math.max(from, to);
  const lo = Math.min(from, to);
  if (hi <= 0) return 0;
  return Math.trunc(((hi - lo) * 100) / hi);
}

/** True when a single move is too large for this market to have really happened. */
export function isSuspectStep(from: number, to: number): boolean {
  return stepMovePercent(from, to) > CUT_PCT;
}

export interface JourneyPoint {
  /** viewBox coordinates */
  x: number;
  y: number;
  price: number;
  changedAt: string;
  /** the step arriving AT this point is not credible (never true for the first) */
  suspect: boolean;
  /** whole-percent size of the step arriving at this point (0 for the first) */
  movePercent: number;
}

export interface Journey {
  points: JourneyPoint[];
  /** `d` for the trend line */
  linePath: string;
  /** `d` for the soft fill under it */
  areaPath: string;
  /** one `d` per implausible segment, drawn over the line to mark it */
  suspectPaths: string[];
  suspectCount: number;
  width: number;
  height: number;
}

/**
 * The drawing surface, in unitless viewBox coordinates. 4:1 so the SVG can be
 * given a matching `aspect-[4/1]` and scale UNIFORMLY — `preserveAspectRatio`
 * defaults to uniform, which is what keeps the current-price dot a circle
 * instead of an ellipse at narrow widths.
 */
export const JOURNEY_VIEWBOX = { width: 640, height: 160, pad: 18 } as const;

/**
 * Turn the recorded events into geometry.
 *
 * `null` for fewer than two points: one price is a position, not a trajectory,
 * and there is no honest line to draw through it. The caller renders that case
 * as prose rather than as a chart — and, importantly, NOT as "no price changes",
 * because one recorded event is one real recorded change.
 *
 * Events arrive oldest-first from `GET /listings/{id}/price-history`.
 */
export function buildJourney(events: PriceEvent[]): Journey | null {
  if (events.length < 2) return null;

  const { width, height, pad } = JOURNEY_VIEWBOX;
  const prices = events.map((e) => e.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const span = max - min;
  const usableH = height - pad * 2;
  const usableW = width - pad * 2;

  const yFor = (price: number) =>
    // A flat history has no vertical range to scale into; centre it rather than
    // dividing by zero or pinning every point to the floor.
    span === 0 ? height / 2 : pad + (1 - (price - min) / span) * usableH;

  const points: JourneyPoint[] = events.map((e, i) => {
    const prev = i > 0 ? events[i - 1].price : e.price;
    return {
      x: pad + (i / (events.length - 1)) * usableW,
      y: yFor(e.price),
      price: e.price,
      changedAt: e.changed_at,
      suspect: i > 0 && isSuspectStep(prev, e.price),
      movePercent: i > 0 ? stepMovePercent(prev, e.price) : 0,
    };
  });

  const xy = (p: JourneyPoint) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xy(p)}`).join(' ');

  const first = points[0];
  const last = points[points.length - 1];
  const areaPath = `${linePath} L ${last.x.toFixed(2)} ${height} L ${first.x.toFixed(2)} ${height} Z`;

  const suspectPaths = points
    .map((p, i) => (p.suspect ? `M ${xy(points[i - 1])} L ${xy(p)}` : null))
    .filter((d): d is string => d !== null);

  return {
    points,
    linePath,
    areaPath,
    suspectPaths,
    suspectCount: suspectPaths.length,
    width,
    height,
  };
}
